import { useEffect, useState } from 'react';
import { Table, TableHead, TableBody, TableRow, TableCell, Paper } from '@mui/material';
import { createColumnHelper, flexRender, HeaderGroup, Header, Cell, ColumnDef } from '@tanstack/react-table';

import OCheck from '@src/shared/OCheck';
import { useTable } from './useTable';

import styles from './OTable.module.scss';
import { EmptyData } from '@src/assets/icons';
import OEmptyStatus from '../OStatus';

interface OTableProps<T> {
  data: T[];
  columns: any[];
  onSelect?: () => void;
  loadMore?: () => void;
  hasLoadMore?: boolean;
  setSelectedRows: (selectedRows: any) => void;
  tableRef?: any;
  isEmptyFilters: boolean;
}

function OTable<T>({
  data,
  columns,
  onSelect,
  hasLoadMore,
  loadMore,
  tableRef,
  setSelectedRows,
  isEmptyFilters,
}: OTableProps<T>) {
  const columnHelper = createColumnHelper();

  const [dataColumns, setDataColumns] = useState<ColumnDef<T, any>[]>([]);
  const { table, observerRef, rows } = useTable({
    columns: dataColumns,
    data,
    onSelect,
    hasLoadMore,
    loadMore,
  });
  useEffect(() => {
    setDataColumns([
      columnHelper.display({
        id: 'select',
        maxSize: 40,
        minSize: 40,
        cell: ({ row }: any) => (
          <OCheck
            checked={row.getIsSelected()}
            onChange={() => {
              table.getRow(row.id).toggleSelected();
            }}
            className={styles.tableCheckbox}
          />
        ),
      }),
      ...columns,
    ]);
    tableRef.current = table;
  }, [columns]);

  useEffect(() => {
    const selectedRowsData = table.getSelectedRowModel().rows.map((row: any) => row.original);
    setSelectedRows(selectedRowsData);
  }, [table.getSelectedRowModel().rows.length]);

  return (
    <Paper className={styles.oTableContainer}>
      <Table className={styles.oTable}>
        <TableHead className={styles.tableHeaderBg}>
          {table.getHeaderGroups().map((headerGroup: HeaderGroup<T>) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header: Header<T, unknown>) => (
                <TableCell key={header.id} sx={{ fontWeight: 600, width: header.getSize() }}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {rows.map((row: any, index: number) => (
            <TableRow key={row.id} ref={observerRef} className={styles.otableRow}>
              {row.getVisibleCells().map((cell: Cell<T, unknown>) => (
                <TableCell key={cell.id} className={styles.otableCell}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {!rows.length && (
            <TableCell colSpan={dataColumns.length}>
              <OEmptyStatus
                image={<EmptyData />}
                title={isEmptyFilters ? 'No attendees have registered the webinar yet' : 'No matching results found.'}
              />
            </TableCell>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default OTable;
